<template>
  <div>
    <div v-if="!messageSent && !showFeatureTable">
      <AlertBanner v-if="showErrorBanner" id="submit-error-alert" :status="0" class="mb-16 mt-8">
        {{ errorMessage }}
      </AlertBanner>
      <RecruitNotBasic
        v-if="!isBasic"
        v-model="message"
        :recruit-sitter-tips="premiumTipList"
        :input-label="chatLabelText"
        :message-send-loading="messageSendLoading"
        :no-jobs-exist="noJobsExist"
        :job-post-return-path="jobPostReturnPath"
        :job-options="jobOptions"
        :selected-job="selectedJob"
        :title="premiumTitle"
        @submit="runSendFlow"
        @update:selected-job="selectedJob = $event ?? ''"
      />
      <RecruitNoJobs
        v-if="isBasic && noJobsExist"
        :recruit-sitter-tips-for-basic-users="basicTipList"
        :title-for-basic-users="basicTitleText"
        :is-popup-mode="isPopupMode"
        :upgrade-message="upgradeLabelText"
        :job-post-return-path="jobPostReturnPath"
      />
      <RecruitBasicJobs
        v-if="isBasic && !noJobsExist"
        :recruit-sitter-tips-for-basic-users="basicTipList"
        :title-for-basic-users="basicTitleText"
        :is-popup-mode="isPopupMode"
        :upgrade-message="upgradeLabelText"
        :job-post-return-path="jobPostReturnPath"
        :job-options="jobOptions"
        :selected-job="selectedJob"
        @update:selected-job="selectedJob = $event ?? ''"
        @recruit-clicked="runSendFlow()"
      />
    </div>

    <div v-else-if="showFeatureTable" class="feature-table">
      <RecruitLimit @upgrade-click="rememberViewedProvider" />
    </div>

    <RecruitSitterSentView
      v-else-if="messageSent && selectedJobUuid"
      :recipient-name="recipientName"
      :recipient-uuid="recipientUuid"
      :is-basic="isBasic"
      :is-popup-mode="isPopupMode"
      @continue-searching="emit('continue-searching')"
    />

    <MessagesNewSent
      v-else-if="messageSent"
      :recipient-uuid="recipientUuid"
      :is-popup-mode="isPopupMode"
      @continue-searching="emit('continue-searching')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import RecruitLimit from '@/apps/messages/new/RecruitLimit.vue';
import RecruitNotBasic from '@/apps/messages/new/RecruitNotBasic.vue';
import RecruitBasicJobs from '@/apps/messages/new/RecruitBasicJobs.vue';
import RecruitNoJobs from '@/apps/messages/new/RecruitBasicNoJobs.vue';
import { getSharedApolloClient } from '@/lib/apollo_setup';
import { sendSegmentEvent } from '@/lib/segment';
import MessagesNewSent from './MessagesNewSent.vue';
import RecruitSitterSentView from './RecruitSitterSentView.vue';
import { basicTips, basicTitle, chatLabel, nonBasicTitle, premiumTips, upgradeLabel } from './recruitSitterContent';
import { useRecruitFlow } from './useRecruitFlow';
import { useRecruitJobs } from './useRecruitJobs';
import type { JobEdge } from '@/graphql/messages/queries';
import type { Account } from '@/models/account';
import type { SuccessPayload } from './types';

defineOptions({ name: 'RecruitSitter' });

const props = withDefaults(
  defineProps<{
    account: Account;
    recipientUuid: string;
    recipientName: string;
    jobs?: JobEdge[] | null;
    searchCareType?: string;
    isPopupMode?: boolean;
  }>(),
  {
    jobs: null,
    searchCareType: '',
    isPopupMode: false,
  }
);

const emit = defineEmits<{
  (e: 'success', payload: SuccessPayload): void;
  (e: 'continue-searching'): void;
  (e: 'discard-check', hasDraft: boolean): void;
}>();

const message = ref('');
const isBasic = computed(() => props.account.accountType() === 'basic');
const jobs = computed(() => props.jobs ?? null);
const searchCareType = computed(() => props.searchCareType);
const recipientUuid = computed(() => props.recipientUuid);

const { selectedJob, noJobsExist, jobOptions, selectedJobUuid, prepareJobs } = useRecruitJobs({
  jobs,
  searchCareType,
  isBasic,
  getClient: getSharedApolloClient,
});

const { messageSendLoading, messageSent, showFeatureTable, showErrorBanner, errorMessage, runSendFlow, rememberViewedProvider } =
  useRecruitFlow({
    recipientUuid,
    isBasic,
    selectedJobUuid,
    getClient: getSharedApolloClient,
    onSuccess: (payload) => emit('success', payload),
  });

const copyInput = computed(() => ({
  recipientName: props.recipientName,
  isPopupMode: props.isPopupMode,
  noJobsExist: noJobsExist.value,
  isBasic: isBasic.value,
}));
const chatLabelText = computed(() => chatLabel(props.recipientName));
const upgradeLabelText = computed(() => upgradeLabel(props.recipientName));
const basicTitleText = computed(() => basicTitle(copyInput.value));
const premiumTitle = computed(() => nonBasicTitle(copyInput.value));
const basicTipList = computed(() => basicTips(copyInput.value));
const premiumTipList = computed(() => premiumTips(copyInput.value));
const jobPostReturnPath = computed(() => encodeURIComponent(window.location.pathname + window.location.search));

watch(message, (newVal) => {
  emit('discard-check', newVal.length > 0);
});

onMounted(async () => {
  await prepareJobs();
  // Keep event name stable for existing analytics dependencies.
  sendSegmentEvent('Recruit Sitter to Job Flow Initiated', {
    sitter_user_id: props.recipientUuid,
    parent_user_id: props.account?.id,
    parent_subscription_status: props.account?.accountType(),
    has_open_job: !noJobsExist.value,
  });
});
</script>
